import React, { FC } from 'react';

import { RoverLoading } from '@rover/react-lib/src/components';
import {
  apiFrontendServiceLandingLocaleFaqsList,
  getApiFrontendServiceLandingLocaleFaqsListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-service-landing-locale-faqs-list';
import { useApiGroomingSearchRetrieve } from '@rover/rsdk/src/apiClient/latest/api-grooming-search-retrieve';
import {
  apiServiceLinksRetrieve,
  getApiServiceLinksRetrieveQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-service-links-retrieve';
import type { ApiServiceLinksRetrieve200 } from '@rover/rsdk/src/apiClient/latest/model/apiServiceLinksRetrieve200';
import useServiceLinksShim from '@rover/shared/js/hooks/useServiceLinksShim';
import { Http500Error } from '@rover/utilities/HttpError';
import { getCountryUrlPrefix, US_COUNTRY_URL_PREFIX } from '@rover/utilities/roverGeo.constants';

import GroomingServiceLandingPageTemplate from '../../templates/GroomingServiceLandingPageTemplate/GroomingServiceLandingPageTemplate';
import { GROOMING_LANDING_SEARCH_PARAMS } from '../../templates/GroomingServiceLandingPageTemplate/utils/groomingLandingSearch';
import { ServiceLandingContainerProps, ServiceLandingEngineContainer } from '../../types';
import useServiceLandingPageData from '../../utils/useServiceLandingPageData';

const EMPTY_SERVICE_LINKS: ApiServiceLinksRetrieve200 = { data: [] };

const GroomingServiceLandingContainer: FC<ServiceLandingContainerProps> = ({
  serviceLandingData,
  descriptor,
}) => {
  const { countryUrlPrefix } = descriptor;
  const {
    citiesAndNeighborhoods,
    countryConfig,
    countryFooterLinks,
    countryRegionLinks,
    locale,
    isLoading: isLoadingPageData,
    isError: isErrorPageData,
  } = useServiceLandingPageData(descriptor, serviceLandingData);

  const { data: groomingSearchResponse } = useApiGroomingSearchRetrieve(
    GROOMING_LANDING_SEARCH_PARAMS
  );

  const { serviceLinksWithDisplayName } = useServiceLinksShim();

  if (isErrorPageData) throw new Http500Error();

  if (isLoadingPageData) {
    return <RoverLoading style={{ height: '100vh' }} data-testid="rover-loading" isLoading />;
  }

  if (!countryConfig || !countryRegionLinks) return null;

  const brandServiceLinks = (serviceLinksWithDisplayName ?? []).filter(
    ({ href }) => (getCountryUrlPrefix(href) ?? US_COUNTRY_URL_PREFIX) === countryUrlPrefix
  );

  return (
    <GroomingServiceLandingPageTemplate
      serviceLandingData={serviceLandingData}
      descriptor={descriptor}
      serviceLinksWithDisplayName={brandServiceLinks}
      countryConfig={countryConfig}
      locale={locale}
      groomingSearchResponse={groomingSearchResponse}
      citiesAndNeighborhoodsLinks={citiesAndNeighborhoods}
      countryFooterLinks={countryFooterLinks}
      countryRegionLinks={countryRegionLinks}
    />
  );
};

const reactQueryPrefetch: ServiceLandingEngineContainer['reactQueryPrefetch'] = (
  queryClient,
  { descriptor: { countryUrlPrefix, language, serviceLandingSlug } }
) =>
  Promise.all([
    queryClient.prefetchQuery({
      queryKey: getApiServiceLinksRetrieveQueryKey(),
      queryFn: () => apiServiceLinksRetrieve().catch(() => EMPTY_SERVICE_LINKS),
    }),
    queryClient.prefetchQuery({
      queryKey: getApiFrontendServiceLandingLocaleFaqsListQueryKey(
        countryUrlPrefix,
        language,
        serviceLandingSlug
      ),
      queryFn: () =>
        apiFrontendServiceLandingLocaleFaqsList(countryUrlPrefix, language, serviceLandingSlug),
    }),
  ]);

export default Object.assign(GroomingServiceLandingContainer, { reactQueryPrefetch });
